/**
 * Common Validation Schemas
 * Reusable primitives and base schemas for consistent validation across the application
 */

import { z } from 'zod';

/**
 * UUID Schema
 * Validates UUID v4 format
 */
export const uuidSchema = z.string().uuid('Invalid UUID format');

/**
 * Email Schema
 * Validates email format with normalization
 */
export const emailSchema = z.string()
  .email('Invalid email address')
  .toLowerCase()
  .trim()
  .max(255, 'Email must be less than 255 characters');

/**
 * Phone Schema
 * Validates Australian phone numbers and normalizes format
 */
export const phoneSchema = z.string()
  .regex(/^(?:\+?61|0)[2-478](?:[ -]?[0-9]){8}$/, 'Invalid Australian phone number')
  .transform(val => val.replace(/\s|-/g, ''));

/**
 * URL Schema
 * Validates URLs
 */
export const urlSchema = z.string().url('Invalid URL format');

/**
 * Secure URL Schema
 * Validates URLs with HTTPS requirement in production
 */
export const secureUrlSchema = z.string().url('Invalid URL format').refine(
  url => process.env.NODE_ENV !== 'production' || url.startsWith('https://'),
  'HTTPS required in production environment'
);

/**
 * Date String Schema
 * Validates ISO 8601 date strings (Supabase PostgreSQL format)
 * Accepts dates with timezone offset
 */
export const dateStringSchema = z.string().datetime({ offset: true });

/**
 * Date Schema (flexible)
 * Accepts Date object or ISO string
 */
export const dateSchema = z.union([
  z.date(),
  z.string().datetime({ offset: true }).transform(str => new Date(str)),
]);

/**
 * Future Date Schema
 * Validates that date is in the future
 */
export const futureDateSchema = dateStringSchema.refine(
  date => new Date(date) > new Date(),
  'Date must be in the future'
);

/**
 * Past Date Schema
 * Validates that date is in the past
 */
export const pastDateSchema = dateStringSchema.refine(
  date => new Date(date) < new Date(),
  'Date must be in the past'
);

/**
 * Slug Schema
 * Validates URL-safe slugs (lowercase, alphanumeric, hyphens)
 */
export const slugSchema = z.string()
  .min(3, 'Slug must be at least 3 characters')
  .max(50, 'Slug must be no more than 50 characters')
  .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens')
  .regex(/^[a-z0-9]/, 'Slug must start with a letter or number')
  .regex(/[a-z0-9]$/, 'Slug must end with a letter or number')
  .refine(slug => !slug.includes('--'), 'Slug cannot contain consecutive hyphens');

/**
 * Color Hex Schema
 * Validates hex color codes (#RGB, #RRGGBB, #RRGGBBAA)
 */
export const colorHexSchema = z.string()
  .regex(/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/, 'Invalid hex color format');

/**
 * Pagination Schema
 * Standard pagination parameters
 */
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sort_by: z.string().optional(),
  sort_order: z.enum(['asc', 'desc']).default('asc'),
});

export type Pagination = z.infer<typeof paginationSchema>;

/**
 * Search Query Schema
 * Standard search parameters
 */
export const searchQuerySchema = z.object({
  q: z.string().min(1, 'Search query is required').max(500),
  fields: z.array(z.string()).optional(),
  fuzzy: z.boolean().default(false),
});

export type SearchQuery = z.infer<typeof searchQuerySchema>;

/**
 * ID Parameter Schema
 * For route parameters
 */
export const idParamSchema = z.object({
  id: uuidSchema,
});

/**
 * Non-Empty String Schema
 * String that cannot be empty or just whitespace
 */
export const nonEmptyStringSchema = z.string()
  .min(1, 'This field is required')
  .trim()
  .refine(val => val.length > 0, 'This field cannot be empty');

/**
 * Optional String Transform
 * Transforms empty strings to null
 */
export const optionalStringSchema = z.string()
  .optional()
  .transform(val => val === '' ? null : val);

/**
 * Trimmed String Schema
 * Automatically trims whitespace
 */
export const trimmedStringSchema = z.string().trim();

/**
 * Money Schema
 * Validates monetary amounts (2 decimal places)
 */
export const moneySchema = z.coerce.number()
  .nonnegative('Amount must be positive')
  .multipleOf(0.01, 'Amount must have at most 2 decimal places');

/**
 * Percentage Schema
 * Validates percentage (0-100)
 */
export const percentageSchema = z.coerce.number()
  .min(0, 'Percentage must be at least 0')
  .max(100, 'Percentage cannot exceed 100');

/**
 * Positive Integer Schema
 */
export const positiveIntSchema = z.coerce.number()
  .int('Must be a whole number')
  .positive('Must be greater than 0');

/**
 * Non-Negative Integer Schema
 */
export const nonNegativeIntSchema = z.coerce.number()
  .int('Must be a whole number')
  .nonnegative('Must be 0 or greater');

/**
 * File Size Schema
 * Validates file size in bytes (max 10MB by default)
 */
export const fileSizeSchema = (maxBytes: number = 10 * 1024 * 1024) => z.number()
  .int()
  .positive()
  .max(maxBytes, `File size must be less than ${maxBytes / 1024 / 1024}MB`);

/**
 * File Type Schema
 * Validates MIME types
 */
export const fileTypeSchema = (allowedTypes: string[]) => z.string()
  .refine(
    type => allowedTypes.includes(type),
    `File type must be one of: ${allowedTypes.join(', ')}`
  );

/**
 * Image MIME Types
 */
export const imageMimeTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
] as const;

/**
 * Document MIME Types
 */
export const documentMimeTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

/**
 * JSON String Schema
 * Validates and parses JSON strings
 */
export const jsonStringSchema = z.string().transform((str, ctx) => {
  try {
    return JSON.parse(str);
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid JSON format',
    });
    return z.NEVER;
  }
});

/**
 * Timestamp Range Schema
 * Validates start and end timestamps
 */
export const timestampRangeSchema = z.object({
  start: dateStringSchema,
  end: dateStringSchema,
}).refine(
  data => new Date(data.end) > new Date(data.start),
  {
    message: 'End date must be after start date',
    path: ['end'],
  }
);

export type TimestampRange = z.infer<typeof timestampRangeSchema>;
